import { HttpErrorResponse } from '@angular/common/http';
import { effect, inject } from '@angular/core';
import { Params } from '@angular/router';
import { ComplianceControllerService, TransferComplianceDto } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withFeature, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { SelectionModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.component';
import { DateTimeService } from '@shared/datetime/datetime.service';
import { SupportedTimezone } from '@shared/datetime/datetime.utils';
import { toApiTimezone } from '@shared/pipes/translate-timezone.pipe';
import { DownloadStateService } from '@shared/services/download-state.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { withUrlFilter } from '@shared/store/url/with-url-filter';
import { today, tomorrow } from '@shared/utils/datetime';
import { toSelectionModel, urlParamToArray } from '@shared/utils/url-params';
import { getDateTimeAsString, isDefined } from '@stillmandigital/common';
import { filter, pipe, switchMap } from 'rxjs';
import { SharedComplianceStore } from '../shared-compliance.store';

export const COMPLIANCE_TRANSFERS_HISTORY_DOWNLOAD_KEY = 'compliance-transfers-history-download';
export const COMPLIANCE_TRANSFERS_HISTORY_ROUTE_ID = 'complianceTransfersHistoryRouteId';

export interface ComplianceTransfersHistoryFilter {
  account?: SelectionModel;
  asset?: SelectionModel;
  source?: TransferComplianceDto.TransferSourceEnum[];
  status?: TransferComplianceDto.StatusEnum[];
  jurisdiction?: string;
  businessEntity?: TransferComplianceDto.BusinessEntityEnum;
  fromTime?: string;
  toTime?: string;
}

interface ComplianceTransfersHistoryState {
  transfers: TransferComplianceDto[];
  pageFilter: ComplianceTransfersHistoryFilter | undefined;
}

const initialState: ComplianceTransfersHistoryState = {
  transfers: [],
  pageFilter: {
    fromTime: getDateTimeAsString(today(), '00:00:00'),
    toTime: getDateTimeAsString(tomorrow(), '00:00:00'),
  },
};

export const ComplianceTransfersHistoryStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    _complianceService: inject(ComplianceControllerService),
    _sharedComplianceStore: inject(SharedComplianceStore),
    _dateTimeService: inject(DateTimeService),
    _notificationService: inject(NotificationService),
    _downloadStateService: inject(DownloadStateService),
  })),
  withMethods(() => {
    const _pageFilterToUrlParams = (pageFilter: ComplianceTransfersHistoryFilter): Params => ({
      accountInclude: pageFilter.account?.include,
      accountExclude: pageFilter.account?.exclude,
      assetInclude: pageFilter.asset?.include,
      assetExclude: pageFilter.asset?.exclude,
      source: pageFilter.source,
      status: pageFilter.status,
      jurisdiction: pageFilter.jurisdiction,
      businessEntity: pageFilter.businessEntity,
      fromTime: pageFilter.fromTime,
      toTime: pageFilter.toTime,
    });

    const _urlParamsToPageFilter = (params: Params): ComplianceTransfersHistoryFilter => ({
      account:
        params['accountInclude'] || params['accountExclude']
          ? toSelectionModel(params['accountInclude'], params['accountExclude'])
          : undefined,
      asset:
        params['assetInclude'] || params['assetExclude']
          ? toSelectionModel(params['assetInclude'], params['assetExclude'])
          : undefined,
      source: urlParamToArray<TransferComplianceDto.TransferSourceEnum>(params['source']),
      status: urlParamToArray<TransferComplianceDto.StatusEnum>(params['status']),
      jurisdiction: params['jurisdiction'],
      businessEntity: params['businessEntity'] as TransferComplianceDto.BusinessEntityEnum,
      fromTime: params['fromTime'],
      toTime: params['toTime'],
    });

    return { _pageFilterToUrlParams, _urlParamsToPageFilter };
  }),
  withFeature(() => withUrlFilter<ComplianceTransfersHistoryFilter>(COMPLIANCE_TRANSFERS_HISTORY_ROUTE_ID)),
  withMethods(store => {
    const updatePageFilter = (pageFilter: Partial<ComplianceTransfersHistoryFilter>) =>
      patchState(store, { pageFilter: { ...(store.pageFilter() ?? {}), ...pageFilter } });

    const _loadTransfers = rxMethod<ComplianceTransfersHistoryFilter>(
      pipe(
        filter(isDefined),
        switchMap(pageFilter =>
          store._complianceService
            .getTransfers2({
              accountId: pageFilter.account?.include,
              excludeAccountIds: pageFilter.account?.exclude,
              assetId: pageFilter.asset?.include,
              excludedAssetIds: pageFilter.asset?.exclude,
              fromTime: pageFilter.fromTime && store._dateTimeService.toAPIDate(pageFilter.fromTime),
              toTime: pageFilter.toTime && store._dateTimeService.toAPIDate(pageFilter.toTime),
              source: pageFilter.source,
              status: pageFilter.status,
              jurisdiction: pageFilter.jurisdiction,
              businessEntity: pageFilter.businessEntity,
              sortDirection: 'DESC',
              skipCount: 0,
            })
            .pipe(
              tapResponse({
                next: transfers => patchState(store, { transfers }),
                error: (error: HttpErrorResponse) =>
                  store._notificationService.showErrorNotification(`The transfers history couldn't be loaded`, error),
              }),
            ),
        ),
      ),
    );

    const reloadTransfers = () => {
      const pageFilter = store.pageFilter();
      if (isDefined(pageFilter)) {
        _loadTransfers(pageFilter);
      }
    };

    const downloadTransfersHistoryCsv = rxMethod<{ customZone: SupportedTimezone }>(
      pipe(
        switchMap(({ customZone }) => {
          const pageFilter = store.pageFilter();
          store._downloadStateService.start(COMPLIANCE_TRANSFERS_HISTORY_DOWNLOAD_KEY);

          return store._complianceService
            .transferReport1({
              accountId: pageFilter?.account?.include,
              excludeAccountIds: pageFilter?.account?.exclude,
              assetId: pageFilter?.asset?.include,
              excludedAssetIds: pageFilter?.asset?.exclude,
              fromTime: pageFilter?.fromTime ? store._dateTimeService.toAPIDate(pageFilter.fromTime) : undefined,
              toTime: pageFilter?.toTime ? store._dateTimeService.toAPIDate(pageFilter.toTime) : undefined,
              source: pageFilter?.source,
              status: pageFilter?.status,
              customZone: toApiTimezone(customZone),
              jurisdiction: pageFilter?.jurisdiction,
              businessEntity: pageFilter?.businessEntity,
            })
            .pipe(
              tapResponse({
                next: (response: { id: number }) => {
                  store._sharedComplianceStore.addPendingDownload({
                    id: response.id,
                    fileName: 'compliance-transfers-history.csv',
                    downloadKey: COMPLIANCE_TRANSFERS_HISTORY_DOWNLOAD_KEY,
                  });
                },
                error: (error: HttpErrorResponse) => {
                  store._downloadStateService.error(COMPLIANCE_TRANSFERS_HISTORY_DOWNLOAD_KEY, error);
                  store._notificationService.showErrorNotification(
                    `The transfers history couldn't be downloaded`,
                    error,
                  );
                },
              }),
            );
        }),
      ),
    );

    return { updatePageFilter, reloadTransfers, downloadTransfersHistoryCsv };
  }),
  withHooks({
    onInit(store) {
      effect(() => store.reloadTransfers());
    },
    onDestroy(store) {
      store._downloadStateService.clear(COMPLIANCE_TRANSFERS_HISTORY_DOWNLOAD_KEY);
    },
  }),
);
